// systems/bestiary.js
// Bestiary system - tracks mob discovery, stats, and loot in the Tome of Echoes

import { COMBAT_ZONES, MOBS, getMob, getMobLoot, getMobMechanic } from '../skills/combat/combatData.js';
import { getBountyMark, BOUNTY_MARKS } from '../skills/bounty/bountyData.js';
import { gameState } from '../game/gameState.js';
import { isDiscovered } from './tomeOfEchoes.js';

/**
 * Zone display order - sorted by tier then level
 */
export const BESTIARY_ZONE_ORDER = Object.values(COMBAT_ZONES)
    .sort((a, b) => a.tier - b.tier || a.level - b.level)
    .map(z => z.id);

/**
 * Check if a mob has been discovered (killed at least once)
 * @param {string} mobId
 * @returns {boolean}
 */
export function isMobDiscovered(mobId) {
    return gameState.getKillCount(mobId) > 0;
}

/**
 * Get overall bestiary statistics across all mobs
 * @returns {{ total: number, discovered: number, percentage: number }}
 */
export function getBestiaryStats() {
    const allMobs = Object.values(MOBS);
    const total = allMobs.length;
    let discovered = 0;
    for (const mob of allMobs) {
        if (isMobDiscovered(mob.id)) discovered++;
    }
    const percentage = total > 0 ? Math.round((discovered / total) * 100) : 0;
    return { total, discovered, percentage };
}

/**
 * Get all mob IDs for a zone, handling tiered zones (Ashen Crucible)
 * @param {object} zone - Zone object
 * @returns {string[]} Flat array of all mob IDs in the zone
 */
function getAllZoneMobIds(zone) {
    if (zone.tiers) {
        const ids = [];
        for (const tier of Object.values(zone.tiers)) {
            ids.push(...tier.mobs);
        }
        return ids;
    }
    return zone.mobs || [];
}

/**
 * Get bestiary stats for a specific zone
 * @param {string} zoneId
 * @returns {object|null} Zone stats with mob list and tier info
 */
export function getZoneBestiaryStats(zoneId) {
    const zone = Object.values(COMBAT_ZONES).find(z => z.id === zoneId);
    if (!zone) return null;

    const allMobIds = getAllZoneMobIds(zone);
    let discovered = 0;

    const mobs = allMobIds.map(mobId => {
        const mob = getMob(mobId);
        if (!mob) return null;
        const kills = gameState.getKillCount(mobId);
        if (kills > 0) discovered++;
        return {
            ...mob,
            killCount: kills,
            discovered: kills > 0
        };
    }).filter(Boolean);

    const total = mobs.length;
    const percentage = total > 0 ? Math.round((discovered / total) * 100) : 0;

    const result = {
        id: zoneId,
        name: zone.name,
        tier: zone.tier,
        level: zone.level,
        color: zone.color,
        total,
        discovered,
        percentage,
        mobs
    };

    // Handle tiered zones (Ashen Crucible)
    if (zone.tiers) {
        result.tiers = Object.entries(zone.tiers).map(([key, tierData]) => ({
            key,
            name: tierData.name,
            mobIds: tierData.mobs
        }));
    }

    return result;
}

/**
 * Get bestiary stats for all zones in display order
 * @returns {object[]} Array of zone stats
 */
export function getAllZoneBestiaryStats() {
    return BESTIARY_ZONE_ORDER.map(zoneId => getZoneBestiaryStats(zoneId)).filter(Boolean);
}

/**
 * Search mobs by name (only discovered mobs match by name, undiscovered still show as ???)
 * @param {string} query
 * @returns {object[]} Array of mob objects with discovery status
 */
export function searchMobs(query) {
    if (!query || query.trim().length === 0) return [];

    const lowerQuery = query.toLowerCase().trim();
    const allMobs = Object.values(MOBS);

    return allMobs
        .filter(mob => mob.name.toLowerCase().includes(lowerQuery))
        .map(mob => {
            const kills = gameState.getKillCount(mob.id);
            return {
                ...mob,
                killCount: kills,
                discovered: kills > 0
            };
        });
}

/**
 * Get full mob details for the detail overlay
 * @param {string} mobId
 * @returns {object|null} Aggregated mob data
 */
export function getMobFullDetails(mobId) {
    const mob = getMob(mobId);
    if (!mob) return null;

    const killCount = gameState.getKillCount(mobId);
    const mark = getBountyMark(mobId);
    const nextMark = mark.rank < BOUNTY_MARKS.length - 1 ? BOUNTY_MARKS[mark.rank + 1] : null;
    const mechanic = getMobMechanic(mobId);
    const researchLevel = gameState.getMobResearchLevel(mobId);
    const weaknessRevealed = gameState.isMobWeaknessRevealed(mobId);
    const resistanceRevealed = gameState.isMobResistanceRevealed(mobId);
    const zone = findZoneForMob(mobId);

    // Get loot with discovery status
    const rawLoot = getMobLoot(mobId, zone?.id);
    const lootHistory = gameState.getMobLootHistory(mobId);

    const enrichDrop = (drop) => ({
        ...drop,
        itemDiscovered: isDiscovered(drop.itemId),
        totalObtained: lootHistory[drop.itemId] || 0
    });

    const loot = {
        common: (rawLoot.common || []).map(enrichDrop),
        rare: (rawLoot.rare || []).map(enrichDrop)
    };

    return {
        ...mob,
        killCount,
        bountyMark: mark,
        nextMark,
        mechanic,
        researchLevel,
        weaknessRevealed,
        resistanceRevealed,
        zone: zone ? { id: zone.id, name: zone.name, tier: zone.tier, level: zone.level } : null,
        loot
    };
}

/**
 * Find which zone a mob belongs to
 * @param {string} mobId
 * @returns {object|null} Zone object
 */
export function findZoneForMob(mobId) {
    for (const zone of Object.values(COMBAT_ZONES)) {
        if (zone.tiers) {
            for (const tier of Object.values(zone.tiers)) {
                if (tier.mobs.includes(mobId)) return zone;
            }
        } else if (zone.mobs && zone.mobs.includes(mobId)) {
            return zone;
        }
    }
    return null;
}

export default {
    BESTIARY_ZONE_ORDER,
    isMobDiscovered,
    getBestiaryStats,
    getZoneBestiaryStats,
    getAllZoneBestiaryStats,
    searchMobs,
    getMobFullDetails,
    findZoneForMob
};
